import classNames from "classnames";
import { ButtonPrimary } from "components/Clickable/Buttons";
import { ErrorMessage } from "components/Input/ErrorMessage";
import { PasswordInputGroup } from "components/Input/Input";
import { writeCMSDataClientSide } from "helpers/api";
import { processServerSideErrors } from "helpers/forms";
import urls from "helpers/urls";
import { FormBase } from "interfaces";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import styles from "./change-password-form.module.scss";

interface Form extends FormBase {
    new_password: string;
    passwordMatch: string;
}

export const ChangePasswordLoggedOutForm = () => {
    const {
        register,
        clearErrors,
        getValues,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting },
    } = useForm<Form>();
    const { query, push } = useRouter();
    const [message, setMessage] = useState<string | JSX.Element>("");

    const onSubmit: SubmitHandler<Form> = async (data) => {
        const uidb64 = query.uidb64;
        const token = query.token;

        const response = await writeCMSDataClientSide(
            "POST",
            `/api/auth/password-request-confirm`,
            {},
            { ...data, uidb64, token },
        );
        if (response.ok) {
            push(urls.accountOverview);
        } else {
            processServerSideErrors(response, data, setError);
            if (response.status === 400 && errors.server?.message) {
                setMessage(
                    <>
                        Having problems? Try{" "}
                        <Link href={urls.forgotPassword}>
                            entering your email address again.
                        </Link>
                    </>,
                );
            }
        }
    };

    return (
        <div>
            <h2 className={classNames(["h4", styles.heading])}>
                Change Password
            </h2>
            <form
                className={styles.form}
                noValidate
                onSubmit={(event) => {
                    clearErrors();
                    handleSubmit(onSubmit)(event);
                }}
            >
                <PasswordInputGroup
                    {...register("new_password", {
                        required: {
                            value: true,
                            message: "Enter a new password.",
                        },
                    })}
                    label="New Password"
                    errorMessage={errors.new_password?.message}
                />
                <PasswordInputGroup
                    {...register("passwordMatch", {
                        required: {
                            value: true,
                            message: "Reenter your new password.",
                        },
                        validate: (value) =>
                            getValues().new_password !== value
                                ? "Your passwords do not match."
                                : true,
                    })}
                    label="Confirm New Password"
                    errorMessage={errors.passwordMatch?.message}
                />
                <ButtonPrimary
                    className={styles.fullButtonMargin}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                >
                    Change Password
                </ButtonPrimary>
            </form>
            {errors.server?.message && (
                <ErrorMessage>{errors.server.message}</ErrorMessage>
            )}
            <p className={styles.footerLinks} aria-live="polite">
                {message}
            </p>
        </div>
    );
};
